import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { getExtraFieldById, getHistoricalOrderSelectOptions, updateExtraField } from "../api/extraFieldApi";
import { getExtraFieldFormSchema } from "../schema/extraFieldSchema";
import { SurveyImportExtraFieldFormDto } from "../types";
import { ExtraFieldObjectTypeEnum } from "../types/enums";
import { useUserPermissions } from "../../../../utils/auth";
import { PERMISSIONS } from "../../../../constants/permissions";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

export default function ExtraFieldForm() {
    const { t } = useTranslation(["common", "extraField"]);
    const { extraFieldId } = useParams<{ extraFieldId: string }>();
    const navigate = useNavigate();
    const permissions  = useUserPermissions();
    const canEdit = permissions.length > 0 && (permissions.includes(PERMISSIONS.EXTRA_FIELDS.EDIT) || permissions.includes(PERMISSIONS.EXTRA_FIELDS.CREATE));

    const [orderOptions, setOrderOptions] = useState<SelectItemDto[]>([])
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [formError, setFormError] = useState<string | null>(null);
    const [extraFieldForm, setExtraFieldForm] = useState<SurveyImportExtraFieldFormDto>({
        id: 0,
        fieldName: "",
        description: "",
        isFilterable: false,
        showInHistorical: false,
        orderInHistorical: undefined,
        showInDetails: false,
        orderInDetails: undefined,
        objectType: 1,
        canEditHistoricalFields: false,
        hasResultsRelated: false
    });

    const { showModal: showConfirmUpdateModal, modalData, openModal: openConfirmUpdateModal, closeModal } = useModal();

    useEffect(() => {
        getHistoricalOrderSelectOptions()
            .then(setOrderOptions)
            .catch((error: ApiErrorResponse) => {
                setFormError(error.message ?? t("common:requestError.errorBadRequest"));
            });
            
        if (!extraFieldId) 
            return;

        getExtraFieldById(parseInt(extraFieldId))
            .then(setExtraFieldForm)
            .catch((error: ApiErrorResponse) => {
                setFormError(error.message ?? t("common:requestError.errorBadRequest"));
            });
    }, [extraFieldId, t]);

    const handleSubmit = async () => {
        setErrors({});
        setFormError(null);

        const schema = getExtraFieldFormSchema(t);

        try {
            await schema.validate(extraFieldForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        try {
            if (!extraFieldForm.id) {
                setFormError(t("common:requestError.errorBadRequest"));
                return;
            }
            await updateExtraField(extraFieldForm.id, extraFieldForm);
            navigate("/admin/extraFields", {
                state: { message: t("extraField:form.messages.updateSuccess"), shouldRefresh: true }
            });

        } catch (error: any) {
            console.error(error);
            setFormError(t("common:requestError.errorBadRequest"));
        }
    };

    const getObjectTypeOptions = () => {
        if (extraFieldForm.objectType == ExtraFieldObjectTypeEnum.NPS) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.NPS}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.NPS]}`)}
                </option>
            );
        }
        if (extraFieldForm.objectType == ExtraFieldObjectTypeEnum.TextForEvaluateAI) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.TextForEvaluateAI}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.TextForEvaluateAI]}`)}
                </option>
            );
        }
        if (extraFieldForm.objectType == ExtraFieldObjectTypeEnum.Campaign) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.Campaign}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.Campaign]}`)}
                </option>
            );
        }
        if (extraFieldForm.objectType == ExtraFieldObjectTypeEnum.CampaignDescription) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.CampaignDescription}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.CampaignDescription]}`)}
                </option>
            );
        }
        if (extraFieldForm.fieldName == "AnswerDate" || extraFieldForm.fieldName == "FieldCreateDate") {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.InfoDate}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.InfoDate]}`)}
                </option>
            );
        }
        else {
            return(<>
                <option key={0} value={ExtraFieldObjectTypeEnum.Info}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.Info]}`)}
                </option>
                <option key={1} value={ExtraFieldObjectTypeEnum.InfoDate}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.InfoDate]}`)}
                </option>
            </>);
        }
    }

    return (
        <>
            <ModalGlobal
                show={showConfirmUpdateModal}
                onClose={closeModal}
                subtitle={modalData.subtitle}
                contentType={modalData.type}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                onSave={handleSubmit}
                isLoading={false}
            />
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md={8}>
                    <h2>{t("extraField:form.title")}</h2>
                </Col>
                <Col xs={12} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={() => {
                            if (!canEdit) return;
                            extraFieldForm.hasResultsRelated
                                ? openConfirmUpdateModal(null, "", t("extraField:form.messages.updateWarning"), "info", "")
                                : handleSubmit();
                        }}
                        onCancelClick={() => navigate("/admin/extraFields")}
                        cancelLabel={t("common:buttons.buttonBack")}
                        disabled={!canEdit}
                    />
                </Col>
            </Row>

            {formError && (
                <Row>
                    <Col xs={12}>
                        <div className="alert alert-danger">{formError}</div>
                    </Col>
                </Row>
            )}

            <Row>
                <Col xs={12} md={8}>
                    <Form>
                        <Row className="mb-3">
                            <Col xs={12} md={4}>
                                <Form.Group>
                                    <Form.Label>{t("extraField:form.fields.fieldName")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={t(`extraField:field.${extraFieldForm.fieldName}`)}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col xs={12} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("extraField:form.fields.objectType")}</Form.Label>
                                    <Form.Select
                                        value={extraFieldForm.objectType}
                                        isInvalid={!!errors.objectType}
                                        onChange={(e) => {
                                            setExtraFieldForm((prev) => ({
                                                ...prev,
                                                objectType: Number(e.target.value),
                                            }));
                                        }}
                                        disabled={!canEdit}
                                    >
                                        {getObjectTypeOptions()}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.objectType}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label>{t("extraField:form.fields.description")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={extraFieldForm.description ?? ""}
                                        isInvalid={!!errors.description}
                                        onChange={(e) => {
                                            setExtraFieldForm((prev) => ({
                                                ...prev,
                                                description: e.target.value
                                            }));
                                        }}
                                        disabled={!canEdit}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.description}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col xs={12} md={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="extra-field-is-filterable"
                                    label={t("extraField:form.fields.isFilterable")}
                                    checked={extraFieldForm.isFilterable}
                                    onChange={(e) => {
                                        setExtraFieldForm((prev) => ({
                                            ...prev,
                                            isFilterable: e.target.checked,
                                        }));
                                    }}
                                        disabled={!canEdit}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="extra-field-show-in-details"
                                    label={t("extraField:form.fields.showInDetails")}
                                    checked={extraFieldForm.showInDetails}
                                    onChange={(e) => {
                                        setExtraFieldForm((prev) => ({
                                            ...prev,
                                            showInDetails: e.target.checked,
                                        }));
                                    }}
                                        disabled={!canEdit}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col xs={12} md={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="extra-field-show-in-historical"
                                    label={t("extraField:form.fields.showInHistorical")}
                                    checked={extraFieldForm.showInHistorical}
                                    disabled={!extraFieldForm.canEditHistoricalFields || !canEdit}
                                    onChange={(e) => {
                                        setExtraFieldForm((prev) => ({
                                            ...prev,
                                            showInHistorical: e.target.checked,
                                            orderInHistorical: e.target.checked ? prev.orderInHistorical : Number(""),
                                        }));
                                    }}
                                />
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("extraField:form.fields.orderInHistorical")}</Form.Label>                                    
                                    <Form.Select
                                        value={extraFieldForm.orderInHistorical}
                                        isInvalid={!!errors.orderInHistorical}
                                        disabled={!extraFieldForm.canEditHistoricalFields || !canEdit || !extraFieldForm.showInHistorical}
                                        onChange={(e) => {
                                            setExtraFieldForm((prev) => ({
                                                ...prev,
                                                orderInHistorical: Number(e.target.value),
                                            }));
                                        }}
                                    >
                                        <option key={-1} value={""}></option>
                                        {orderOptions.map((option) => (
                                            <option key={option.id} value={option.id}>{option.name}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.orderInHistorical}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={3}>
                                <Form.Group>
                                    <Form.Label>{t("extraField:form.fields.columnName")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={extraFieldForm.historicalColumnName ?? ""}
                                        isInvalid={!!errors.historicalColumnName}
                                        onChange={(e) => {
                                            setExtraFieldForm((prev) => ({
                                                ...prev,
                                                historicalColumnName: e.target.value
                                            }));
                                        }}
                                        disabled={!extraFieldForm.canEditHistoricalFields || !canEdit || !extraFieldForm.showInHistorical}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.historicalColumnName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}
